/**
 * App logger. No-op in production for info/debug; errors always surface.
 * Use this instead of `console.*` anywhere in features/widgets/entities/shared.
 *
 * Reads `process.env.NODE_ENV` directly rather than going through the `core/`
 * env module — `shared/` must not depend on `core/`. The check is the same
 * one Next.js inlines at build time, so production bundles still strip the
 * debug/info/warn paths via dead-code elimination.
 */
type LogArgs = readonly unknown[];

interface Logger {
  debug: (...args: LogArgs) => void;
  info: (...args: LogArgs) => void;
  warn: (...args: LogArgs) => void;
  error: (...args: LogArgs) => void;
}

const noop = (): void => {
  /* intentionally empty */
};

const IS_PRODUCTION = process.env.NODE_ENV === "production";

export const logger: Logger = IS_PRODUCTION
  ? {
      debug: noop,
      info: noop,
      warn: noop,
      error: (...args: LogArgs) => {
        console.error(...args);
      },
    }
  : {
      debug: (...args: LogArgs) => {
        console.debug(...args);
      },
      info: (...args: LogArgs) => {
        console.info(...args);
      },
      warn: (...args: LogArgs) => {
        console.warn(...args);
      },
      error: (...args: LogArgs) => {
        console.error(...args);
      },
    };
